import Image from 'next/future/image';
import Link from 'next/link';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { HotDealCardContentT, TopDealProduct } from './types';
import { priceFormater } from '@/settings';
import { calculateSavings, getOfferUri } from '@/lib/offer';
import { checkDateEquality } from './helpers';

const CardHeader = dynamic(() => import('./CardHeader'), {
  ssr: false,
});

const Savings = ({
  price,
  comparePrice,
}: {
  price: number;
  comparePrice?: number | null;
}) => {
  if (!comparePrice) return null;
  const percentageSaving = calculateSavings(price, comparePrice);

  if (percentageSaving > 3) {
    return (
      <div className="mb-2 ml-2 flex items-center justify-center rounded-full bg-red px-2 py-0.5 text-[11px] text-white xs:mb-0 xs:ml-1 sm:ml-0">
        Save <span className="ml-1 font-semibold">{percentageSaving}%</span>
      </div>
    );
  }

  return null;
};

const Content = ({
  deal,
  isCollectionPage,
  isSpotlight,
}: HotDealCardContentT) => {
  const { comparePrice, price, imageUrl, title } = deal;
  const savingsPercentage = calculateSavings(price, comparePrice);
  const shouldShowComparePrice =
    comparePrice && comparePrice > price && savingsPercentage > 3;

  return (
    <div className="h-full w-full rounded-md border border-t-0 p-1.5 xs:p-3">
      <div
        className={cn('flex h-full gap-3', {
          'flex-col 3xs:flex-row': isSpotlight,
          'flex-col': !isSpotlight,
        })}
      >
        <div className="flex-1 px-3">
          <div className="relative h-full min-h-[100px] w-full">
            <Image
              src={imageUrl}
              alt={title}
              className="h-full w-full object-contain"
              fill
            />
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div>
            <h3
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="three-line-ellipsis text-sm"
            >
              {title.replace(/Oppo/g, 'OPPO')}
            </h3>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex flex-col justify-center ">
                <h3 className="text-sm font-semibold">
                  {priceFormater.long(price)}
                </h3>
                {shouldShowComparePrice && comparePrice && (
                  <h4 className="text-xs text-slate-gray-300 line-through">
                    {priceFormater.long(comparePrice)}
                  </h4>
                )}
              </div>
              {shouldShowComparePrice && (
                <div
                  className={cn({
                    'hidden sm:block': isCollectionPage,
                  })}
                >
                  <Savings price={price} comparePrice={comparePrice} />
                </div>
              )}
            </div>
            {isSpotlight && (
              <button
                type="button"
                className="mt-5 h-10 w-full rounded-md !bg-gray-700 text-xs font-semibold text-white"
              >
                Shop now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function DealProductBox({
  deal,
  className,
  isCollectionPage = false,
  deliveryBy,
  isSpotlight = false,
}: {
  deal: TopDealProduct;
  index: number;
  className?: string;
  isCollectionPage?: boolean;
  deliveryBy?: boolean;
  isSpotlight?: boolean;
}) {
  const { dealtypes, dealStockLimit, dealEndDate, categories } = deal;

  const isBundle =
    (!!dealtypes &&
      !!dealtypes.find((dealtype) => dealtype === 'Bundle Deals')) ||
    !!categories?.find((category) => category.includes('Bundle'));

  const { isSameDate } = checkDateEquality(dealEndDate);

  return (
    <>
      <Link href={getOfferUri(deal as any, deliveryBy)} passHref key={deal.gid}>
        <a
          className={cn(
            'relative flex h-full w-full cursor-pointer items-start rounded-md bg-white text-sm sm:flex-col',
            className,
            {
              'flex-col-reverse': isCollectionPage,
              'flex-col': !isCollectionPage,
            },
          )}
        >
          <div
            className={cn('h-8 w-full border border-b-0 border-gray-200 p-2', {
              'bg-gradient-to-r from-[#E4FEFF] via-[#F9FFFF] to-[#F5FFFF]':
                isBundle || isSameDate || dealStockLimit,
              'rounded-b-md sm:rounded-b-md sm:rounded-t-md': isCollectionPage,
              'rounded-t-md': !isCollectionPage,
            })}
          >
            <CardHeader
              unitsLeft={dealStockLimit}
              isBundle={isBundle}
              isSameDate={isSameDate}
            />
          </div>
          <Content
            isSpotlight={isSpotlight}
            deal={deal}
            isCollectionPage={isCollectionPage}
          />
        </a>
      </Link>
    </>
  );
}
